/**
 * useShareBoost Hook
 * Manages Twitter share boost state and actions
 */

import { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../contexts/WalletProvider';
import { useReferral } from '../contexts/ReferralContext';
import * as shareApi from '../services/shareApiService';

interface UseShareBoostReturn {
  // Status
  hasClaimedBoost: boolean;
  hasUnusedBoost: boolean;
  sessionIdUsed: string | null; // Which session consumed the boost
  boostValue: number;
  isLoading: boolean;
  
  // Session tracking (don't show modal twice in same session)
  shownThisSession: boolean;
  markShownThisSession: () => void;
  
  // Actions
  claimBoost: () => Promise<{ success: boolean; error?: string }>;
  refreshStatus: () => Promise<void>;
  
  // Tweet helpers
  getTweetText: () => string;
  getTweetUrl: () => string;
  openTweetIntent: () => void;
}

export function useShareBoost(): UseShareBoostReturn {
  const { address } = useWallet();
  const { myReferralCode, getMyCode } = useReferral();
  
  const [hasClaimedBoost, setHasClaimedBoost] = useState(false);
  const [hasUnusedBoost, setHasUnusedBoost] = useState(false);
  const [sessionIdUsed, setSessionIdUsed] = useState<string | null>(null);
  const [boostValue, setBoostValue] = useState(1.5);
  const [isLoading, setIsLoading] = useState(false);
  const [shownThisSession, setShownThisSession] = useState(false);
  
  /**
   * Refresh boost status from backend
   */
  const refreshStatus = useCallback(async () => {
    if (!address) return;
    
    setIsLoading(true);
    try {
      const result = await shareApi.getBoostStatus(address);
      
      if (result.success && result.data) {
        setHasClaimedBoost(result.data.hasClaimedBoost);
        setHasUnusedBoost(result.data.hasUnusedBoost);
        setSessionIdUsed(result.data.sessionIdUsed || null);
        if (result.data.boostDetails?.value) {
          setBoostValue(result.data.boostDetails.value);
        }
      }
    } catch (error) {
      console.error('Error refreshing share boost status:', error);
    } finally {
      setIsLoading(false);
    }
  }, [address]);
  
  /**
   * Claim the share boost
   */
  const claimBoost = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!address) {
      return { success: false, error: 'Wallet not connected' };
    }
    
    setIsLoading(true);
    try {
      const result = await shareApi.claimBoost(address, 'twitter_round1_win');
      
      if (result.success) {
        setHasClaimedBoost(true);
        setHasUnusedBoost(true);
        if (result.boost?.value) {
          setBoostValue(result.boost.value);
        }
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Error claiming share boost:', error);
      return { success: false, error: 'Failed to claim boost' };
    } finally {
      setIsLoading(false);
    }
  }, [address]);
  
  /**
   * Mark modal as shown this session
   */
  const markShownThisSession = useCallback(() => {
    setShownThisSession(true);
  }, []);
  
  /**
   * Generate tweet text with referral link
   */
  const getTweetText = useCallback(() => {
    const referralLink = myReferralCode 
      ? `https://app.feen.bet/${myReferralCode}`
      : 'https://app.feen.bet';
    
    return `bro.. @FEENTIME might be the craziest thing ive played
just won my first race and doubled my sol in 15s lol
game is invite only rn
early access 🗣

${referralLink}`;
  }, [myReferralCode]);
  
  /**
   * Get Twitter intent URL
   */
  const getTweetUrl = useCallback(() => {
    const text = getTweetText();
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
  }, [getTweetText]);
  
  /**
   * Open Twitter intent in new tab
   * Ensures referral code is generated first
   */
  const openTweetIntent = useCallback(async () => {
    let referralCode = myReferralCode;
    
    // If no referral code yet, generate one first
    if (!referralCode) {
      // getMyCode updates context state, but we need the code immediately
      // So we call the API directly here as well
      const { getMyCode: getMyCodeApi } = await import('../services/referralApiService');
      if (address) {
        const result = await getMyCodeApi(address);
        if (result.success && result.data?.code) {
          referralCode = result.data.code;
        }
      }
    }
    
    // Build tweet with the referral code we have
    const referralLink = referralCode 
      ? `https://app.feen.bet/${referralCode}`
      : 'https://app.feen.bet';
    
    const tweetText = `bro.. @FEENTIME might be the craziest thing ive played
just won my first race and doubled my sol in 15s lol
game is invite only rn
early access 🗣

${referralLink}`;
    
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [myReferralCode, address]);
  
  /**
   * Load status on mount and when wallet changes
   */
  useEffect(() => {
    if (address) {
      refreshStatus();
      // Also ensure we have the referral code for the tweet
      if (!myReferralCode) {
        getMyCode();
      }
    }
  }, [address, refreshStatus, myReferralCode, getMyCode]);
  
  return {
    hasClaimedBoost,
    hasUnusedBoost,
    sessionIdUsed,
    boostValue,
    isLoading,
    shownThisSession,
    markShownThisSession,
    claimBoost,
    refreshStatus,
    getTweetText,
    getTweetUrl,
    openTweetIntent
  };
}

export default useShareBoost;

